import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Select,
  Switch,
  Text,
} from '@chakra-ui/react'
import CustomTooltip from 'brokertslib/src/components/CustomTooltip'
import type {
  AvailableAddons,
  InsurerProductPartAvailable,
  Offer,
  OfferLeasing,
} from 'BrokerTsLib/src/dtos'
import { Insurer, Risk } from 'BrokerTsLib/src/enums'
import { useEffect } from 'react'

import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { useGetOfferAvailableAddons } from '../../hooks/useOffer'
import { Loading } from '../Loading'
import { QuestionMarkIcon } from '../QuestionMarkIcon'

export const QuoteItemSettingsForm = ({
  offer,
  addons,
  setAddons,
}: {
  offer: OfferLeasing | Offer
  addons: any
  setAddons: React.Dispatch<any>
}) => {
  const {
    data,
    isLoading,
    refetch: refetchAvailableAddons,
  } = useGetOfferAvailableAddons(offer.offerId)
  const { isDisabled } = useInsuranceParams()
  const insurerId = offer.insurerId

  const onSelectAddon = (
    riskId: number,
    insurerProductId?: number,
    insurerInternalId?: string,
  ) => {
    setAddons((x: any) => {
      return { ...x, [riskId]: { riskId, insurerProductId, insurerInternalId } }
    })
  }

  const onRemoveAddon = (riskId: number) => {
    setAddons((x: any) => {
      return { ...x, [riskId]: {} }
    })
  }

  useEffect(() => {
    refetchAvailableAddons() // po rekalkulaci musim refreshnout - mohly se zmenit
  }, [offer.dateCalculated])

  const availableAddons = (data?.data ?? []) as AvailableAddons[]

  return (
    <>
      {availableAddons.length == 0 && isLoading && <Loading large={true} />}
      <Grid
        sx={{
          gridTemplateColumns: { lg: 'repeat(2, 1fr)' },
          columnGap: 8,
          rowGap: 8,
          alignItems: 'start',
        }}
      >
        {availableAddons.length == 0 && !isLoading && (
          <Text>Žádné připojištění není k dispozici</Text>
        )}
        {availableAddons.length > 0 &&
          availableAddons
            .sort((x, y) => x.group.displayOrder - y.group.displayOrder)
            .map((risk) => {
              const selectedAddon = addons[risk.group.riskId]
              const value = risk.items.find(
                (x) =>
                  (x.insurerProductId &&
                    x.insurerProductId == selectedAddon?.insurerProductId) ||
                  (x.insurerInternalId &&
                    x.insurerInternalId == selectedAddon?.insurerInternalId),
              )
              const dataSource =
                risk.items.map((item, index) => {
                  return { i: index + 1, ...item }
                }) ?? []

              // switch
              if (dataSource.length == 1) {
                const part = dataSource[0]

                return (
                  <FormControl sx={{ gap: 4 }} key={risk.group.id}>
                    <GroupLabel
                      label={risk.group.name}
                      tooltip={
                        <Box
                          dangerouslySetInnerHTML={{
                            __html: risk.group.description ?? '',
                          }}
                        ></Box>
                      }
                    />

                    <FormControl display="flex" alignItems="center">
                      <Switch
                        id="liquidation"
                        colorScheme="green"
                        size="lg"
                        dir="rtl"
                        isDisabled={isDisabled}
                        isChecked={!!value}
                        onChange={(e) => {
                          if (e.target.checked)
                            onSelectAddon(
                              risk.group.riskId,
                              part.insurerProductId,
                              part.insurerInternalId,
                            )
                          else onRemoveAddon(risk.group.riskId)
                        }}
                      />
                    </FormControl>
                  </FormControl>
                )
              }

              // slider
              // if (risk.group.riskId != Risk.NAHR && dataSource.every(x => TryParseInt(x.limit) != undefined)) {
              //   const min = dataSource[0]
              //   const max = dataSource[dataSource.length - 1]
              //   return (
              //     <FormControl sx={{ gap: 4 }} key={risk.group.id}>
              //       <SliderLabel
              //         label={risk.group.name}
              //         value={value?.limit ? priceFormat(parseInt(value.limit)) : "Bez připojištění"}
              //       />

              //       <CustomSlider
              //         value={dataSource.find(x => x.insurerProductId == value?.insurerProductId || x.insurerInternalId && x.insurerInternalId == selectedAddon.insurerInternalId)?.i ?? 0}
              //         labels={[
              //           { label: min?.limit ?? '', value: min?.i },
              //           { label: max?.limit ?? '', value: max?.i },
              //         ]}
              //         min={0}
              //         max={dataSource.length - 1}
              //         step={1}
              //         onChange={(val) => {
              //           const selectedItem = dataSource.find(x => x.i == val)
              //           onSelectAddon(risk.group.riskId, selectedItem?.insurerProductId, selectedItem?.insurerInternalId)
              //         }}
              //       />
              //     </FormControl>
              //   )
              // }

              const dropDownDs: (InsurerProductPartAvailable & {
                i?: number
              })[] = []
              let pushBezPojisteni = true

              if (
                (risk.group.riskId == Risk.ASISTENCE &&
                  insurerId != Insurer.GeneraliCeskaPojistovna) || // pro asistenci nepřidáváme - kromě GČP
                (risk.group.riskId == Risk.BALICEK &&
                  insurerId == Insurer.GeneraliCeskaPojistovna) || // pro balíky GČP také ne
                dataSource.findIndex((x) => x.name == 'Bez připojištění') >= 0
              ) {
                // nebo pokud uz v dataSource je, tak nepridavam po druhe
                pushBezPojisteni = false
              }

              if (pushBezPojisteni) {
                dropDownDs.push({
                  name: 'Bez připojištění',
                  price: 0,
                  priceFrequency: 0,
                  id: 0,
                  insurerInternalId: '',
                })
              }

              // Specialita Uniqa AIV - za určitých podmínek je možnost připojištění čelního skla zdarma
              // V datasource existují 2 položky se stejným názvem, ale jedna je zdarma, tu je třeba označit
              if (
                risk.group.riskId == Risk.SKLA &&
                insurerId == Insurer.Uniqa &&
                dataSource.findIndex((x) => x.price > 0)
              ) {
                const itemSklaZdarma = dataSource.find(
                  (x) => x.price == 0 && x.name.indexOf('sklo') != -1,
                )
                if (itemSklaZdarma) {
                  itemSklaZdarma.name = itemSklaZdarma.name + ' (zdarma)'
                }
              }

              dropDownDs.push(...dataSource)

              // dropdownlist
              return (
                <FormControl sx={{ gap: 4 }} key={risk.group.id}>
                  <GroupLabel
                    label={risk.group.name}
                    tooltip={
                      <Text
                        dangerouslySetInnerHTML={{
                          __html: risk.group.description ?? '',
                        }}
                      ></Text>
                    }
                  />

                  <FormControl
                    display="flex"
                    flexDirection="column"
                    alignItems="start"
                    width="100%"
                  >
                    <Select
                      sx={{ maxWidth: { md: '295px' } }}
                      variant="outline"
                      isDisabled={isDisabled}
                      value={
                        dropDownDs.find(
                          (x) =>
                            (x.insurerProductId &&
                              x.insurerProductId == value?.insurerProductId) ||
                            (x.insurerInternalId &&
                              x.insurerInternalId == value?.insurerInternalId),
                        )?.i
                      }
                      onChange={(e) => {
                        const selectedItem = dropDownDs.find(
                          (x) => x.i == parseInt(e.target.value),
                        )
                        onSelectAddon(
                          risk.group.riskId,
                          selectedItem?.insurerProductId,
                          selectedItem?.insurerInternalId,
                        )
                      }}
                    >
                      {dropDownDs.map((x) => (
                        <option
                          key={x.i ? x.i.toString() : `default${risk.group.id}`}
                          value={x.i ? x.i.toString() : ''}
                        >
                          {x.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </FormControl>
              )
            })}
      </Grid>
    </>
  )
}

const GroupLabel = ({
  label,
  tooltip,
}: {
  label: string
  tooltip?: string | React.ReactNode
}) => (
  <Flex sx={{ w: '100%', justifyContent: 'space-between' }}>
    <FormLabel>{label}</FormLabel>

    {tooltip && (
      <CustomTooltip
        trigger="clickHover"
        maxW="500px"
        hasArrow
        label={tooltip}
        placement="auto"
      >
        <Box sx={{ lineHeight: 0 }}>
          <QuestionMarkIcon ml={2} />
        </Box>
      </CustomTooltip>
    )}
  </Flex>
)
